import { IAppComponentProps } from '../../components';
import { Form, Input, message, Modal, Spin, Tooltip } from 'antd';
import { useGetAllUserGuideGroupsQuery } from './queries.generated';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { GridApi, ICellRendererParams, ColDef } from 'ag-grid-community';
import {
    useCreateUserGuideGroupMutation,
    useDeleteUserGuideGroupMutation,
    useUpdateUserGuideGroupMutation,
} from './mutations.generated';
import { useAppDispatch, useAppSelector } from '../../main/hooks';
import { AgGridReact } from 'ag-grid-react';
import { invalidateUserGuideCache } from './HelpUserGuideService';
import { triggerLanguageChange } from '../../store/IEDPGlobalSlice';
import { getI18n } from '../../utils/Utils';
import { DeleteOutlined, EditOutlined, PlusSquareOutlined } from '@ant-design/icons';
import { UserGuideGroupQuery } from './UserGuideManagement';

const UserGuideGroupManagement = ({ intl, currentUser }: IAppComponentProps) => {
    const [form] = Form.useForm();
    const { data, loading, error, refetch } = useGetAllUserGuideGroupsQuery();
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const [editingGroup, setEditingGroup] = useState<any>(null);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [manualLoading, setManualLoading] = useState<boolean>(false);
    const [gridApi, setGridApi] = useState<GridApi>(undefined);
    const [updateUserGuideGroup] = useUpdateUserGuideGroupMutation();
    const [createUserGuideGroup] = useCreateUserGuideGroupMutation();
    const [deleteUserGuideGroup] = useDeleteUserGuideGroupMutation();
    const dispatch = useAppDispatch();
    const { rerenderAfterLanguageChange } = useAppSelector((state) => state.globalStateReducer);

    const gridRef = useRef<AgGridReact>(null);

    const showModal = () => {
        setIsModalVisible(true);
        setIsEditing(false);
        form.resetFields();
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        setEditingGroup(null);
        form.resetFields();
    };

    useEffect(() => {
        if (data) {
            refetch().finally(() => setManualLoading(false));
        }
    }, [rerenderAfterLanguageChange]);

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            setManualLoading(true);

            if (isEditing && editingGroup) {
                await updateUserGuideGroup({
                    variables: {
                        input: {
                            userGuideGroupId: editingGroup.userGuideGroupId,
                            sortOrder: editingGroup.sortOrder,
                            translationRequest: {
                                phraseID: editingGroup.userGuideGroupNamePhraseId,
                                translations: [
                                    { language: 'en', content: values.englishTranslation },
                                    { language: 'de', content: values.germanTranslation }
                                ]
                            }
                        }
                    }
                });
                message.success(intl.formatMessage({ id: 'Download Success' }));
            } else {
                const generatePhraseID = (englishText: string): string => {
                    const prefix = 'user-guide-group-';
                    const sanitizedText = englishText
                        .toLowerCase()
                        .replace(/\s+/g, '-') // Replace spaces with hyphens
                        .replace(/[^a-z\-]/g, '') // Remove all characters that are not lowercase letters or hyphens
                        .replace(/--+/g, '-') // Replace multiple consecutive hyphens with a single hyphen
                        .trim() // Remove leading and trailing whitespace
                        .replace(/^-|-$/g, ''); // Remove leading and trailing hyphens

                    const randomNumber = Math.floor(Math.random() * 9) + 1; // Generate a random number between 1 and 9

                    return `${prefix}${sanitizedText}-${randomNumber}`;
                };

                await createUserGuideGroup({
                    variables: {
                        input: {
                            sortOrder: data?.getAllUserGuideGroups.length + 1,
                            translationRequest: {
                                phraseID: generatePhraseID(values.englishTranslation),
                                translations: [
                                    { language: 'en', content: values.englishTranslation },
                                    { language: 'de', content: values.germanTranslation }
                                ]
                            }
                        }
                    }
                });
                message.success(intl.formatMessage({ id: 'Download Success' }));
            }

            setIsModalVisible(false);
            invalidateUserGuideCache();
            dispatch(triggerLanguageChange());
        } catch (error) {
            console.error('Error creating/updating user guide group:', error);
            message.error(intl.formatMessage({ id: 'Download Error' }));
            setManualLoading(false);
        }
    };

    const handleEdit = (group: UserGuideGroupQuery) => {
        setIsEditing(true);
        setEditingGroup(group);
        setIsModalVisible(true);
        form.setFieldsValue({
            englishTranslation: group.translations.find(t => t.languageCode.toLowerCase().trim() === 'en')?.translation,
            germanTranslation: group.translations.find(t => t.languageCode.toLowerCase().trim() === 'de')?.translation,
        });
    };

    const handleDelete = async (group: UserGuideGroupQuery) => {
        try {
            setManualLoading(true);
            await deleteUserGuideGroup({
                variables: { id: group.userGuideGroupId }
            });
            message.success(intl.formatMessage({ id: 'Download Success' }));
            refetch().then(_=> invalidateUserGuideCache());
        } catch (error) {
            console.error('Error deleting user guide group:', error);
            message.error(intl.formatMessage({ id: 'Download Error' }));
        } finally {
            setManualLoading(false);
        }
    };

    // Custom cell renderer for the Actions column
    const ActionsCellRenderer = useMemo(
        () => (props: IAppComponentProps & ICellRendererParams) => {
            const { data } = props;
            return (
                <div>
                    <Tooltip title={getI18n('draft-document-row-options-edit', 'Edit', intl)} key={'editUserGuideGroup'}>
                        <EditOutlined
                            style={{ color: '#444444', marginRight: '5px' }}
                            onClick={(_) => handleEdit(data)}
                        />
                    </Tooltip>

                    <Tooltip title={getI18n('delete', 'Delete', intl)} key={'removeUserGuideGroup'}>
                        <DeleteOutlined
                            style={{ color: '#444444' }}
                            onClick={(_) => handleDelete(data)}
                        />
                    </Tooltip>
                </div>
            );
        },
        [data]
    );

    const GROUP_COLUMN_DEFINITIONS: ColDef[] = [
        {
            headerName: intl.formatMessage({
                id: 'document-draft-grid-header-Options',
                defaultMessage: 'Options',
            }),
            field: 'draftDocumentRowOptions',
            colId: 'draftDocumentRowOptions',
            cellRenderer: ActionsCellRenderer,
            width: 100,
            rowDrag: true,
        },
        {
            field: 'sortOrder',
            headerName: intl.formatMessage({ id: 'sort-order' }),
        },
        {
            field: 'userGuideGroupNamePhraseId',
            colId: 'userGuideGroupNamePhraseId',
            headerName: intl.formatMessage({ id: 'phrase-description' }),
            valueGetter: (params) => {
                return intl.formatMessage({ id: params.data.userGuideGroupNamePhraseId });
            },
        },
        {
            field: 'insertUser',
            headerName: intl.formatMessage({ id: 'freight-documents-insert-user' }),
        },
        {
            field: 'insertDate',
            headerName: intl.formatMessage({ id: 'freight-documents-insert-time' }),
        },
        {
            field: 'updateUser',
            headerName: intl.formatMessage({ id: 'freight-documents-update-user' }),
        },
        {
            field: 'updateDate',
            headerName: intl.formatMessage({ id: 'freight-documents-update-time' }),
        },
    ];

    const onGridReady = (params: any) => {
        setGridApi(params.api);
    };

    const doUserGroupUpdates = async (userGuideGroupSortUpdates: UserGuideGroupQuery[]) => {
        setManualLoading(true);
        try {
            for (const update of userGuideGroupSortUpdates) {
                await updateUserGuideGroup({
                    variables: {
                        input: {
                            userGuideGroupId: update.userGuideGroupId,
                            sortOrder: update.sortOrder,
                            translationRequest: {
                                phraseID: update.userGuideGroupNamePhraseId,
                                translations: update.translations.map((item) => {
                                    return {
                                        language: item.languageCode,
                                        content: item.translation,
                                    };
                                }),
                            },
                        },
                    },
                });
            }
            message.success(intl.formatMessage({ id: 'Download Success' }));
            return true;
        } catch (error) {
            console.error('Error updating user guide groups:', error);
            message.error(intl.formatMessage({ id: 'Download Error' }));
            return false;
        } finally {
            setManualLoading(false);
        }
    };

    const onRowDragEnd = (_event: any) => {
        const userGuideGroupSortUpdates: UserGuideGroupQuery[] = [];

        const rowCount = gridApi.getModel().getRowCount();

        for (let i = 0; i < rowCount; i++) {
            const theNode = gridApi.getModel().getRow(i);
            if (theNode.rowIndex + 1 !== theNode.data.sortOrder) {
                userGuideGroupSortUpdates.push({
                    userGuideGroupId: theNode.data.userGuideGroupId,
                    sortOrder: i + 1,
                    userGuideGroupNamePhraseId: theNode.data.userGuideGroupNamePhraseId,
                    translations: theNode.data.translations.map((item) => {
                        return {
                            languageCode: item.languageCode,
                            phraseId: item.phraseId,
                            translation: item.translation,
                        };
                    }),
                });
            }
        }

        if (userGuideGroupSortUpdates.length > 0) {
            doUserGroupUpdates(userGuideGroupSortUpdates).then((result) => {
                if (result) {
                    refetch().finally(()=>invalidateUserGuideCache());
                }
            });
        }
    };

    return (
        <div>
            <div className="pl15 ag-theme-balham">
                <div style={{margin:"10px 0px"}}>
                    {intl.formatMessage({ id: 'reorder-rows-to-change-sort-order' })}
                </div>
                <Spin spinning={loading || manualLoading}>
                    <Tooltip title={getI18n('add', 'Add', intl)} key={'addUserGuideGroup'}>
                        <PlusSquareOutlined
                            style={{
                                fontSize: '25px',
                                color: '#444444',
                                marginRight: '15px',
                            }}
                            onClick={(_) => showModal()}
                        />
                    </Tooltip>

                    <div style={{ flex: 'auto', height: '500px', paddingTop: '10px' }}>
                        <AgGridReact
                            ref={gridRef}
                            rowData={data?.getAllUserGuideGroups}
                            columnDefs={GROUP_COLUMN_DEFINITIONS}
                            defaultColDef={{
                                sortable: false,
                                filter: false,
                            }}
                            onGridReady={onGridReady}
                            sideBar={false}
                            debug={false}
                            rowDragManaged={true}
                            animateRows={true}
                            onRowDragEnd={onRowDragEnd}
                        />
                    </div>
                </Spin>
            </div>
            <Modal
                title={intl.formatMessage({ id: 'add-or-edit-user-guide-group' }).replace('{0}', isEditing? intl.formatMessage({ id: 'draft-document-row-options-edit' }) : intl.formatMessage({ id: 'add' }))}
                open={isModalVisible}
                onOk={handleOk}
                onCancel={handleCancel}
                confirmLoading={manualLoading}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="englishTranslation"
                        label={intl.formatMessage({ id: 'english-translation' })}
                        rules={[{ required: true, message: intl.formatMessage({ id: 'generic-requiredField-message' }) }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="germanTranslation"
                        label={intl.formatMessage({ id: 'german-translation' })}
                        rules={[{ required: true, message: intl.formatMessage({ id: 'generic-requiredField-message' }) }]}
                    >
                        <Input />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default UserGuideGroupManagement;